import { streamTreeNodes, type StreamTreeNodesOptions } from "@/components/tree/stream-tree-nodes";
import { redirectRefundedTreeRefusal } from "@/lib/refunded-tree-access";
import { NodeModel } from "@/types/models";
import type { ReloadOptions, RequestPayload } from "@inertiajs/core";
import { router } from "@inertiajs/react";

/** Streams the tree on the main thread, where a refunded tree's refusal opens pricing. */
export const streamNodes = (options?: StreamTreeNodesOptions) =>
  streamTreeNodes(options).catch((error: unknown) => {
    redirectRefundedTreeRefusal(error);

    throw error;
  });

const debounce = <TArgs extends unknown[]>(fn: (...args: TArgs) => void, delay: number) => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: TArgs) => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => fn(...args), delay);
  };
};

const translations = {
  "show-branch": "استعراض شجرة",
  "edit-branch": "تعديل شجرة",
  "manage-users": "إدارة مسؤولي العائلة",
  "manage-settings": "التحكم بالإعدادات",
  "manage-statistics": "مشاهدة الإحصائيات",
  family_name: "اسم العائلة",
  created: "إنشاء",
  updated: "تعديل",
  deleted: "حذف",
  restored: "استعادة",
  name: "الاسم",
  life_status: "حي أو متوفى",
  birth: "تاريخ الميلاد",
  death: "تاريخ الوفاة",
  mobile: "رقم الجوال",
  email: "البريد الإلكتروني",
  gender: "الجنس",
  about: "النبذة",
  full_name: "الاسم الكامل",
  nickname: "اللقب",
  marriage: "زواج",
  divorce: "طلاق",
  widow: "وفاة",
} as const;

type TranslationKey = keyof typeof translations;

export const trans = (word: TranslationKey | string, defaultWord: string | null = null): string => {
  return translations[word as TranslationKey] ?? defaultWord ?? word;
};

export const formatLocation = ({ country, city, district }: NodeModel) => {
  let location = "";

  if (country) {
    location += country.name;
  }

  if (city) {
    location += `\n ${city.name}`;
  }

  if (district) {
    location += `\n ${district.name}`;
  }

  return location;
};

export const truncate = (string: string, length = 50) =>
  string?.length > length ? string.substring(0, length) + "..." : string;

export const ordinalsAr = (num: number, isFeminine = false): string => {
  const feminineSuffix = "ة";

  const specialNums: { [key: number]: { masculine: string; feminine: string } } = {
    1: {
      masculine: "الأول",
      feminine: "الأولى",
    },
    10: {
      masculine: "العاشر",
      feminine: "العاشرة",
    },
  };

  const lastDigits: { [key: number]: string } = {
    10: "عشر",
    20: "العشرون",
    30: "الثلاثون",
    40: "الأربعون",
    50: "الخمسون",
    60: "الستون",
    70: "السبعون",
    80: "الثمانون",
    90: "التسعون",
    // you have to add the rest here
  };

  const firstDigits: { [key: number]: string } = {
    1: "الحادي",
    2: "الثاني",
    3: "الثالث",
    4: "الرابع",
    5: "الخامس",
    6: "السادس",
    7: "السابع",
    8: "الثامن",
    9: "التاسع",
  };

  if (specialNums[num]) {
    return isFeminine ? specialNums[num]["feminine"] : specialNums[num]["masculine"];
  }

  if (firstDigits[num]) {
    return isFeminine ? firstDigits[num] + feminineSuffix : firstDigits[num];
  }

  if (lastDigits[num]) {
    return lastDigits[num];
  }

  const firstDigit = num % 10;
  const lastDigit = num - firstDigit;

  if (isFeminine) {
    if (lastDigit < 20) {
      return firstDigits[firstDigit] + feminineSuffix + " " + lastDigits[lastDigit] + feminineSuffix;
    } else {
      return firstDigits[firstDigit] + feminineSuffix + " و" + lastDigits[lastDigit];
    }
  } else {
    if (lastDigit < 20) {
      return firstDigits[firstDigit] + " " + lastDigits[lastDigit];
    }
  }

  return firstDigits[firstDigit] + " و" + lastDigits[lastDigit];
};

export const toEnglishDigits = (str: string) => {
  let e = "۰".charCodeAt(0);
  str = str.replace(/[۰-۹]/g, (t) => (t.charCodeAt(0) - e).toString());

  e = "٠".charCodeAt(0);
  str = str.replace(/[٠-٩]/g, (t) => (t.charCodeAt(0) - e).toString());

  return str.trim();
};

export const debounceReload = debounce((data: RequestPayload, options: ReloadOptions = {}) => {
  router.reload({ ...options, data });
}, 350);
